import React from 'react';

import { studioTheme, hexToRgba, readableInk } from './studioTheme';
import {
  StudioWrap,
  Avatar,
  Identity,
  SourceLogo,
  Stars,
  DateText,
  scoreValue,
  hasSourceLogo,
} from './StudioCardKit';
import { StudioBody, isMediaTestimonial, StudioMediaCard } from './StudioBody';

/**
 * Score Block (widget type 18)
 *
 * The horizontal card in a set of vertical ones: a tinted block on the left
 * leading with the score and the source it came from, the quote and the person
 * on the right. Where the studio pattern used a customer metric, this uses the
 * rating we already store, so it needs no new field.
 */
const ScoreBlockCard = (props) => {
  const { cardItem, socialCardType } = props;
  const t = studioTheme(props);

  const score = scoreValue(cardItem);
  /* Its own field, falling back to the wall accent when unset. */
  const block = t.scoreBlockColor || t.brand;
  const blockBg = t.dark ? hexToRgba(block, 0.22) : hexToRgba(block, 0.13);
  const blockInk = t.dark ? '#f2f3f7' : readableInk(block, '#15171c', '#15171c');

  const showBlock = !!score || hasSourceLogo(cardItem);

  /* One body type scale per card: the text testimonial and the video/audio
     summary must read as the same widget. */
  const bodyTextStyle = { fontSize: t.smallSize ? 13.5 : 14, lineHeight: 1.5 };

  /* A video or audio testimonial IS the card - the player already carries
     the name and work title, so none of the chrome below is drawn. Only the
     summary keeps this widget's look, as the panel it extends into. */
  if (isMediaTestimonial(cardItem, socialCardType)) {
    return (
      <StudioMediaCard
        cardItem={cardItem}
        socialCardType={socialCardType}
        t={t}
        enclosure={{
          border: `1px solid ${t.line}`,
          borderRadius: 16,
          background: t.surface,
          /* the wall's custom card colour wins, as it does on the text card */
          ...(t.cardBackgroundObject || {}),
          boxShadow: t.shadow,
        }}
        summaryStyle={bodyTextStyle}
      />
    );
  }

  return (
    <StudioWrap>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 16,
          border: `1px solid ${t.line}`,
          background: t.surface,
          ...(t.cardBackgroundObject || {}),
          boxShadow: t.shadow,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
        }}
      >

        {showBlock ? (
          <div
            style={{
              flex: '0 0 84px',
              background: blockBg,
              color: blockInk,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              padding: '16px 8px',
            }}
          >
            {score ? (
              <>
                <span
                  className={t.fontClass}
                  style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.04em' }}
                >
                  {score}
                </span>
                <Stars t={t} cardItem={cardItem} size={10} />
              </>
            ) : null}
            <SourceLogo cardItem={cardItem} />
          </div>
        ) : null}

        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <StudioBody
            t={t}
            cardItem={cardItem}
            socialCardType={socialCardType}
            mediaRadius={8}
            textStyle={bodyTextStyle}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Avatar cardItem={cardItem} size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Identity t={t} cardItem={cardItem} nameSize={13.5} subSize={12} />
            </div>
          </div>

          <DateText t={t} cardItem={cardItem} />
        </div>
      </div>
    </StudioWrap>
  );
};

export default ScoreBlockCard;
